import React from 'react';
import { ScaleLinear } from 'd3-scale';
import { useDrawingArea, useXScale, useYScale } from '@mui/x-charts/hooks';

import { ApproachProfileItem } from 'ff/debrief/features/ApproachProfileItem';

interface ApproachProfileMarkerProps {
    item: ApproachProfileItem;
    color?: string;
}

export const ApproachProfileMarker: React.FC<ApproachProfileMarkerProps> = ({
    item,
    color = '#E91E63', // Magenta/Pink color to match approach charts
}) => {
    const drawingArea = useDrawingArea();
    const xScale = useXScale() as ScaleLinear<number, number>;
    const yScale = useYScale('altitude') as ScaleLinear<number, number>;

    const distance = item.getDistanceFromRunway();
    const altitude = item.getAltitude();
    const fixName = item.getFixName();

    // Calculate position in the chart
    const x = xScale(distance);
    const y = yScale(altitude);

    // Diamond size (increased from 6)
    const size = 8;

    // Create diamond path
    const diamondPath = `
        M ${x},${y - size}
        L ${x + size},${y}
        L ${x},${y + size}
        L ${x - size},${y}
        Z
    `;

    // Only render if within drawing area bounds
    if (
        x < drawingArea.left ||
        x > drawingArea.left + drawingArea.width ||
        y < drawingArea.top ||
        y > drawingArea.top + drawingArea.height
    ) {
        return null;
    }

    // Format altitude for display (rounded to nearest foot)
    const formattedAltitude = Math.round(altitude).toLocaleString();

    return (
        <g>
            <path
                d={diamondPath}
                fill={color}
                stroke="#000000"
                strokeWidth={1.5}
            />
            {/* Fix name label above the marker */}
            <text
                x={x - 4}
                y={y - size - 12}
                textAnchor="end"
                fontSize="11"
                fill="#000000"
                fontWeight="bold"
            >
                {fixName}
            </text>
            {/* Crossing altitude label below the marker */}
            <text
                x={x - 4}
                y={y - size}
                textAnchor="end"
                fontSize="11"
                fill="#000000"
                fontWeight="normal"
            >
                {formattedAltitude}&apos;
            </text>
        </g>
    );
};

interface ApproachProfileMarkersProps {
    items: ApproachProfileItem[];
    color?: string;
}

export const ApproachProfileMarkers: React.FC<ApproachProfileMarkersProps> = ({
    items,
    color,
}) => {
    return (
        <>
            {items.map((item, index) => (
                <ApproachProfileMarker
                    key={`${item.getFixName()}-${index}`}
                    item={item}
                    color={color}
                />
            ))}
        </>
    );
};

export default ApproachProfileMarkers;
